"use client";

import { Check } from "lucide-react";
import { useState } from "react";
import { Button } from "./Button";
import { AnimateOnScroll, StaggeredList } from "./index";

const scrollToWaitList = () => {
  document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" });
};

const plans = [
  {
    name: "Starter",
    monthlyPrice: "$0",
    yearlyPrice: "$0",
    description: "For individuals exploring spec-driven development",
    features: [
      "Unlimited specs",
      "3 collaborators",
      "50 checkpoints/month",
      "Basic session search",
      "Community support",
    ],
    highlighted: false,
    buttonText: "Get Started",
    buttonVariant: "secondary" as const,
  },
  {
    name: "Professional",
    monthlyPrice: "$24",
    yearlyPrice: "$19",
    period: "/user/month",
    description: "For teams ready to scale",
    features: [
      "Everything in Starter",
      "Unlimited collaborators",
      "Unlimited checkpoints",
      "Advanced session indexing",
      "Approval workflows",
      "Priority support",
    ],
    highlighted: false,
    buttonText: "Start Free Trial",
    buttonVariant: "secondary" as const,
  },
  {
    name: "Team",
    monthlyPrice: "$49",
    yearlyPrice: "$39",
    period: "/user/month",
    description: "For professional product teams",
    features: [
      "Everything in Professional",
      "Custom workflows",
      "Analytics dashboard",
      "API access",
      "SSO / SAML",
      "Dedicated support",
    ],
    highlighted: true,
    buttonText: "Start Free Trial",
    buttonVariant: "primary" as const,
  },
  {
    name: "Enterprise",
    monthlyPrice: "Custom",
    yearlyPrice: "Custom",
    description: "For large organizations",
    features: [
      "Everything in Team",
      "On-premise deployment",
      "Custom integrations",
      "SLA guarantee",
      "Security review",
      "Dedicated CSM",
    ],
    highlighted: false,
    buttonText: "Contact Sales",
    buttonVariant: "secondary" as const,
  },
];

export function Pricing() {
  const [isYearly, setIsYearly] = useState(true);

  return (
    <section id="pricing" className="py-12 sm:py-16 md:py-20 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <AnimateOnScroll
          variant="fade-up"
          className="text-center max-w-[640px] mx-auto mb-8 sm:mb-10 md:mb-12"
        >
          <span className="inline-block text-[10px] sm:text-xs font-semibold text-primary uppercase tracking-[1.5px] mb-3 sm:mb-4">
            Pricing
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mb-3 sm:mb-4 text-text">
            Simple, transparent pricing
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-text-muted leading-relaxed">
            Start free. Scale as your team grows.
          </p>
        </AnimateOnScroll>

        {/* Billing Toggle */}
        <AnimateOnScroll
          variant="fade-in"
          delay={100}
          className="flex justify-center mb-8 sm:mb-10 md:mb-12"
        >
          <div className="inline-flex bg-white border border-border p-1 rounded-lg sm:rounded-xl shadow-sm">
            <button
              onClick={() => setIsYearly(false)}
              aria-pressed={!isYearly}
              className={`px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-md sm:rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                !isYearly
                  ? "bg-primary text-white"
                  : "bg-transparent text-text-muted"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              aria-pressed={isYearly}
              className={`px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-md sm:rounded-lg font-semibold text-xs sm:text-sm transition-all flex items-center gap-1.5 sm:gap-2 ${
                isYearly
                  ? "bg-primary text-white"
                  : "bg-transparent text-text-muted"
              }`}
            >
              Yearly{" "}
              <span className="bg-success text-white px-1.5 sm:px-2 py-0.5 rounded text-[9px] sm:text-[11px] font-bold">
                −20%
              </span>
            </button>
          </div>
        </AnimateOnScroll>

        {/* Pricing Grid */}
        <StaggeredList
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5 md:gap-6"
          staggerDelay={100}
          initialDelay={150}
          duration={500}
        >
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`bg-white border rounded-xl sm:rounded-2xl md:rounded-[20px] p-5 sm:p-6 md:p-8 flex flex-col relative transition-all duration-300 hover:shadow-xl ${
                plan.highlighted
                  ? "border-2 border-primary shadow-[0_20px_50px_rgba(124,58,237,0.15)]"
                  : "border-border"
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 sm:-top-3.5 left-1/2 -translate-x-1/2 bg-primary text-white px-3 sm:px-5 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-bold whitespace-nowrap">
                  Most Popular
                </div>
              )}

              <h3 className="text-lg sm:text-xl font-bold text-text mb-1.5 sm:mb-2">
                {plan.name}
              </h3>
              <div className="mb-1.5 sm:mb-2">
                <span className="text-3xl sm:text-4xl md:text-[44px] font-extrabold text-text tracking-tight">
                  {isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                </span>
                {plan.period && (
                  <span className="text-xs sm:text-sm text-text-muted">
                    {plan.period}
                  </span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-text-muted mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-border">
                {plan.description}
              </p>

              <ul className="mb-6 sm:mb-8 flex-1 space-y-2.5 sm:space-y-3.5">
                {plan.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm text-text-secondary"
                  >
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-success flex-shrink-0 mt-0.5" aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Button
                variant={plan.buttonVariant}
                onClick={
                  plan.buttonText !== "Contact Sales"
                    ? scrollToWaitList
                    : undefined
                }
                href={
                  plan.buttonText === "Contact Sales"
                    ? "mailto:contact@specledger.io"
                    : undefined
                }
                className="w-full justify-center text-xs sm:text-sm"
              >
                {plan.buttonText}
              </Button>
            </div>
          ))}
        </StaggeredList>
      </div>
    </section>
  );
}
